// src/Components/Drawer/CustomDrawer/CustomDrawer.js

import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Portal from '@mui/material/Portal';

// Icons
import CloseIcon from '@mui/icons-material/Close';

import DrawerSidebar from './DrawerSidebar';
import DrawerAlerts from './DrawerAlerts';
import { useDrawerLogic } from '../../../Hooks/useDrawerLogic';
import { SIDEBAR_WIDTH } from './DrawerUtils';
import { MenuResetView, Grid5x5Icon } from '../../../assets/icons/CustomIconsAdd4';

import InfoTab from '../InfoTab/InfoTab';
import InfoTabMenu from '../InfoTab/InfoTabMenu';
import PhotosTabMenu from '../PhotosTab/PhotosTabMenu';
import PhotosTab from '../PhotosTab/PhotosTab';
import DocsTabMenu from '../DocsTab/DocsTabMenu';
import DocsTab from '../DocsTab/DocsTab';
import MarkersTabMenu from '../Markup/MarkersTabMenu';
import MarkupControls from '../Markup/MarkupControls';
import VisHoverTab from '../VisHoverTab/VisHoverTab';

export default function CustomDrawer(props) {
  const {
    activeTab,
    setActiveTab,
    localActiveAction,
    pointsPlaced,
    isSaving,
    tempData,
    isInfoEditing,
    markerDirty,
    infoDirty,
    photosDirty,
    docsDirty,
    photoItems,
    setPhotoItems,
    photoDescriptions,
    setPhotoDescriptions,
    photoSelectedIds,
    setPhotoSelectedIds,
    photoLastSelectedId,
    setPhotoLastSelectedId,
    docItems,
    setDocItems,
    docSelectedIds,
    setDocSelectedIds,
    photosTrigger,
    photosDeleteTrigger,
    docsTrigger,
    docsDeleteTrigger,
    showDeleteConfirm,
    showUnsavedAlert,
    unsavedMessage,
    alert,
    isDirtyGlobal,
    fileInputRef,
    handleHeaderAction,
    handleTabSwitch,
    handleStyleChange,
    handleMarkerFieldChange,
    handleInfoTabChange,
    handlePhotoUpload,
    handleClearPhoto,
    handleConfirmClose,
    handleCancelClose,
    handleConfirmDelete,
    handleCancelDelete,
    draftSiblings,
    rteFontType, // AMENDED: Extract Global RTE Default Font
    rteFontSize, // AMENDED: Extract Global RTE Default Font
  } = useDrawerLogic(props);

  const {
    activeMenu,
    activeTool,
    drawingStyles,
    selectedMarker,
    setIsUIDirty,
    updateMarkerInState,
  } = props;

  // --- Layout, Dragging & Sizing States ---
  const [isAppFullScreen, setIsAppFullScreen] = React.useState(false);
  const [containerPos, setContainerPos] = React.useState({ x: 10, y: 106 });
  const [containerSize, setContainerSize] = React.useState(() =>
    Math.min(800, window.innerHeight - 116),
  );

  const [isDragging, setIsDragging] = React.useState(false);
  const dragInfo = React.useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  const resizeInfo = React.useRef({
    isResizing: false,
    startX: 0,
    startY: 0,
    initialSize: 0,
  });

  React.useEffect(() => {
    if (setIsUIDirty) {
      setIsUIDirty(isDirtyGlobal);
    }
  }, [isDirtyGlobal, setIsUIDirty]);

  const handleActionWrapper = React.useCallback(
    (action) => {
      if (action === 'CANCEL' || action === 'CLOSE' || action === 'HEADER_CLOSE') {
        const effectivelyNew = selectedMarker && selectedMarker.isNew;
        const isUnsavedNew =
          (localActiveAction === 'ADD_NEW' && pointsPlaced) || effectivelyNew;
        const isCurrentlyDirty = isDirtyGlobal || isUnsavedNew;

        if (!isCurrentlyDirty) {
          if (setIsUIDirty) setIsUIDirty(false);
          setIsAppFullScreen(false);
        }
      }
      handleHeaderAction(action);
    },
    [
      handleHeaderAction,
      setIsUIDirty,
      isDirtyGlobal,
      localActiveAction,
      pointsPlaced,
      selectedMarker,
    ],
  );

  // --- ESCAPE KEY LOGIC ---
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !showUnsavedAlert && !showDeleteConfirm) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        if (document.querySelector('.MuiDialog-root')) return;
        e.preventDefault();
        e.stopPropagation();

        if (isAppFullScreen) {
          if (activeTab !== 'NONE') {
            setActiveTab('NONE');
          } else {
            setIsAppFullScreen(false);
            setActiveTab(
              activeTool === 'POLYGON'
                ? 'POLYGON'
                : activeTool === 'LINE'
                  ? 'POLYLINE'
                  : 'MARKERS',
            );
          }
        } else {
          handleActionWrapper('HEADER_CLOSE');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [
    handleActionWrapper,
    showUnsavedAlert,
    showDeleteConfirm,
    isAppFullScreen,
    activeTab,
    activeTool,
    setActiveTab,
  ]);

  // --- DRAG HANDLERS ---
  const handlePointerDown = (e) => {
    if (e.target.closest('.drag-handle') && !isAppFullScreen) {
      dragInfo.current.isDragging = true;
      setIsDragging(true);
      dragInfo.current.startX = e.clientX;
      dragInfo.current.startY = e.clientY;
      dragInfo.current.initialX = containerPos.x;
      dragInfo.current.initialY = containerPos.y;

      try {
        if (e.target.setPointerCapture) {
          e.target.setPointerCapture(e.pointerId);
        }
      } catch (err) {}

      return;
    }

    if (
      e.target.closest('button') ||
      e.target.closest('a') ||
      e.target.closest('.slick-arrow') ||
      e.target.closest('.slick-dots') ||
      e.target.closest('.resize-handle') ||
      e.target.closest('.MuiInputBase-root') ||
      e.target.closest('.MuiCheckbox-root') ||
      e.target.closest('.sortable-image-item') ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA'
    ) {
      return;
    }
  };

  const handlePointerMove = (e) => {
    if (!dragInfo.current.isDragging) return;
    setContainerPos({
      x: dragInfo.current.initialX + (e.clientX - dragInfo.current.startX),
      y: dragInfo.current.initialY + (e.clientY - dragInfo.current.startY),
    });
  };

  const handlePointerUp = (e) => {
    if (!dragInfo.current.isDragging) return;
    dragInfo.current.isDragging = false;
    setIsDragging(false);

    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  // --- RESIZE HANDLERS ---
  const handleResizePointerDown = (e) => {
    if (isAppFullScreen) return;
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = true;
    resizeInfo.current.startX = e.clientX;
    resizeInfo.current.startY = e.clientY;
    resizeInfo.current.initialSize = containerSize;
    try {
      if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    } catch (err) {}
  };

  const handleResizePointerMove = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();

    const deltaX = e.clientX - resizeInfo.current.startX;
    const deltaY = e.clientY - resizeInfo.current.startY;

    const maxDelta = Math.max(deltaX, deltaY);
    const newSize = Math.max(400, resizeInfo.current.initialSize + maxDelta);

    setContainerSize(newSize);
  };

  const handleResizePointerUp = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = false;
    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const stopProp = (e) => {
    e.stopPropagation();
    if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  let sidebarTitle = 'Update';
  if (activeTab === 'MARKERS') sidebarTitle = 'Update Marker';
  if (activeTab === 'POLYGON') sidebarTitle = 'Update Polygon';
  if (activeTab === 'POLYLINE') sidebarTitle = 'Update Polyline';
  if (activeTab === 'VIS_HOVER') sidebarTitle = 'Visibility & Hover';
  if (activeTab === 'INFO') sidebarTitle = 'Update Info';
  if (activeTab === 'PHOTOS') sidebarTitle = 'Update Photos & Clips';
  if (activeTab === 'DOCS') sidebarTitle = 'Update Docs';

  let effectiveTool = activeTool;
  if (activeTab === 'POLYGON') effectiveTool = 'POLYGON';
  else if (activeTab === 'POLYLINE') effectiveTool = 'LINE';
  else if (activeTab === 'MARKERS') effectiveTool = 'MARKER';

  const t = (selectedMarker?.type || '').toLowerCase();
  const tTemp = (tempData?.type || '').toLowerCase();

  const markerExists =
    selectedMarker?.groupHasMarker ||
    tempData?.groupHasMarker ||
    t === 'marker' ||
    tTemp === 'marker' ||
    !!draftSiblings?.marker;
  const polyExists =
    selectedMarker?.groupHasPolygon ||
    tempData?.groupHasPolygon ||
    t === 'polygon' ||
    tTemp === 'polygon' ||
    !!draftSiblings?.polygon;
  const lineExists =
    selectedMarker?.groupHasPolyline ||
    tempData?.groupHasPolyline ||
    t === 'line' ||
    t === 'polyline' ||
    tTemp === 'line' ||
    tTemp === 'polyline' ||
    !!draftSiblings?.line;

  let shapeExists = false;
  if (activeTab === 'MARKERS') shapeExists = markerExists;
  else if (activeTab === 'POLYGON') shapeExists = polyExists;
  else if (activeTab === 'POLYLINE') shapeExists = lineExists;
  else shapeExists = true;

  const isActuallyDrawingCurrentTab =
    localActiveAction === 'ADD_NEW' && activeTool === effectiveTool;
  const isMarkerEditing = localActiveAction === 'ADD_NEW' || localActiveAction === 'EDIT';

  let effectiveMenuAction = localActiveAction;
  if (!shapeExists && !isActuallyDrawingCurrentTab) {
    effectiveMenuAction = 'None';
  } else if (localActiveAction === 'ADD_NEW' && pointsPlaced) {
    effectiveMenuAction = 'EDIT';
  }

  const isNewGroup = !selectedMarker || selectedMarker.isNew;
  const tabsEnabled = !isNewGroup || pointsPlaced;

  React.useEffect(() => {
    // Exclude 'NONE' so it is maintained as a valid state in full-screen
    if (!tabsEnabled && !['MARKERS', 'POLYGON', 'POLYLINE', 'NONE'].includes(activeTab)) {
      setActiveTab(
        activeTool === 'POLYGON'
          ? 'POLYGON'
          : activeTool === 'LINE'
            ? 'POLYLINE'
            : 'MARKERS',
      );
    }
  }, [tabsEnabled, activeTab, setActiveTab, activeTool]);

  // --- DYNAMIC DISPLAY LOGIC FOR COLUMNS ---
  const displayShowFields =
    shapeExists &&
    ((selectedMarker && selectedMarker.id && localActiveAction === 'EDIT') ||
      isActuallyDrawingCurrentTab);
  const displayWaitingForPoint =
    localActiveAction === 'ADD_NEW' &&
    !pointsPlaced &&
    activeTool === effectiveTool &&
    ['MARKERS', 'POLYGON', 'POLYLINE'].includes(activeTab);
  const showPanels = selectedMarker || displayWaitingForPoint;

  const showMarkupNormal = ['MARKERS', 'POLYGON', 'POLYLINE'].includes(activeTab);
  const showVisHoverNormal = activeTab === 'VIS_HOVER';
  const showInfoNormal = activeTab === 'INFO';
  const showDocsNormal = activeTab === 'DOCS';
  const showPhotosNormal = activeTab === 'PHOTOS';

  // In App Full Screen, ALL COLUMNS ARE ALWAYS TRUE so they never disappear.
  const displayMarkup = isAppFullScreen ? true : showMarkupNormal && showPanels;
  const displayVisHover = isAppFullScreen
    ? true
    : showVisHoverNormal && displayShowFields;
  const displayInfo = isAppFullScreen ? true : showInfoNormal && !!selectedMarker;
  const displayDocs = isAppFullScreen ? true : showDocsNormal && !!selectedMarker;
  const displayPhotos = isAppFullScreen ? true : showPhotosNormal && !!selectedMarker;

  // --- RENDER CONDITIONS FOR INDIVIDUAL STATUS BLOCKS ---
  const shouldRenderMarker =
    markerExists || (localActiveAction === 'ADD_NEW' && effectiveTool === 'MARKER');
  const shouldRenderPolygon =
    polyExists || (localActiveAction === 'ADD_NEW' && effectiveTool === 'POLYGON');
  const shouldRenderPolyline =
    lineExists || (localActiveAction === 'ADD_NEW' && effectiveTool === 'LINE');

  // --- WAITING STATES ---
  const isWaitingMarker =
    localActiveAction === 'ADD_NEW' && !pointsPlaced && effectiveTool === 'MARKER';
  const isWaitingPolygon =
    localActiveAction === 'ADD_NEW' && !pointsPlaced && effectiveTool === 'POLYGON';
  const isWaitingPolyline =
    localActiveAction === 'ADD_NEW' && !pointsPlaced && effectiveTool === 'LINE';

  // --- DYNAMIC POSITIONING & SIZING ENGINE ---
  const getPanelLayout = (panelIndex, currentTab) => {
    if (!isAppFullScreen) {
      return {
        left: `${SIDEBAR_WIDTH}px`,
        width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
        isFocused: false,
        zIndex: 5,
      };
    }

    let activeIndex = -1;
    if (['MARKERS', 'POLYGON', 'POLYLINE'].includes(currentTab)) activeIndex = 0;
    else if (currentTab === 'VIS_HOVER') activeIndex = 1;
    else if (currentTab === 'INFO') activeIndex = 2;
    else if (currentTab === 'PHOTOS') activeIndex = 3;
    else if (currentTab === 'DOCS') activeIndex = 4;

    const isFocused = activeIndex === panelIndex;

    // Base layout: every panel gets a strict 20% slice
    const baseLeftPercent = panelIndex * 20;
    let leftPercent = baseLeftPercent;
    let widthPercent = 20;

    if (isFocused) {
      if (panelIndex === 0) {
        widthPercent = 25; // Exaggerate right side by 5%
        leftPercent = 0;
      } else if (panelIndex === 4) {
        widthPercent = 25; // Exaggerate left side by 5%
        leftPercent = 75;
      } else {
        widthPercent = 30; // Exaggerate 5% on both sides
        leftPercent = baseLeftPercent - 5;
      }
    }

    return {
      left: `calc(${SIDEBAR_WIDTH}px + ((100% - ${SIDEBAR_WIDTH}px) * ${leftPercent / 100}))`,
      width: `calc((100% - ${SIDEBAR_WIDTH}px) * ${widthPercent / 100})`,
      isFocused,
      zIndex: isFocused ? 20 : 5, // Elevate focused panel so it sits on top of neighbors
    };
  };

  const markupLayout = getPanelLayout(0, activeTab);
  const visHoverLayout = getPanelLayout(1, activeTab);
  const infoLayout = getPanelLayout(2, activeTab);
  const photosLayout = getPanelLayout(3, activeTab);
  const docsLayout = getPanelLayout(4, activeTab);

  return (
    <Portal>
      <DrawerAlerts
        isSaving={isSaving}
        alert={alert}
        showUnsavedAlert={showUnsavedAlert}
        unsavedMessage={unsavedMessage}
        onCancelClose={handleCancelClose}
        onConfirmClose={handleConfirmClose}
        showDeleteConfirm={showDeleteConfirm}
        activeTab={activeTab}
        onCancelDelete={handleCancelDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          position: 'fixed',
          top: isAppFullScreen ? 0 : containerPos.y,
          left: isAppFullScreen ? 0 : containerPos.x,
          width: isAppFullScreen ? '100%' : `${containerSize}px`,
          height: isAppFullScreen ? '100%' : `${containerSize}px`,
          zIndex: isAppFullScreen ? 90000 : 1600,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          pointerEvents: 'none',
          touchAction: 'none',
          transition:
            isDragging || resizeInfo.current.isResizing ? 'none' : 'all 0.3s ease-in-out',
        }}>
        {/* INNER WRAPPER */}
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* 1. SIDEBAR (Top Left, Always Visible) */}
          <Box
            className='sidebar-strip'
            title={!selectedMarker ? 'UPDATE MARKERS: Add New or click on a marker' : ''}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: SIDEBAR_WIDTH, // 60px
              height: '100%',
              backgroundColor: '#000',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 60, // Always topmost
              pointerEvents: 'auto',
            }}>
            {/* Dedicated Drag Handle */}
            <Box
              className='drag-handle'
              sx={{
                width: '100%',
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                cursor: isAppFullScreen ? 'default' : 'grab',
                opacity: isAppFullScreen ? 0.2 : 0.5,
                pointerEvents: isAppFullScreen ? 'none' : 'auto',
                color: '#fff',
                '&:hover': {
                  opacity: isAppFullScreen ? 0.2 : 1,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
                flexShrink: 0,
              }}
              title={isAppFullScreen ? 'Dragging Disabled' : 'Drag Panel'}>
              <Grid5x5Icon />
            </Box>

            {/* Quick Actions (Close/Reset/Fullscreen) */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: 1,
                mb: 1,
              }}>
              <IconButton
                size='small'
                onClick={() => handleActionWrapper('HEADER_CLOSE')}
                sx={{
                  color: '#fff',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
                title='Close Edit Mode'>
                <CloseIcon fontSize='medium' />
              </IconButton>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.currentTarget.blur();
                  setContainerPos({ x: 10, y: 106 });
                  setContainerSize(Math.min(800, window.innerHeight - 116));
                }}
                sx={{
                  color: '#fff',
                  opacity: isAppFullScreen ? 0.2 : 1,
                  pointerEvents: isAppFullScreen ? 'none' : 'auto',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
                title='Reset Position & Size'>
                <MenuResetView fontSize='medium' />
              </IconButton>
            </Box>

            {/* Render Tab Icons ONLY if not in Full Screen mode */}
            {!isAppFullScreen && (
              <DrawerSidebar
                activeMenu={activeMenu}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleTabSwitch={handleTabSwitch}
                selectedMarker={selectedMarker}
                tempData={tempData}
                draftSiblings={draftSiblings}
                sidebarTitle={sidebarTitle}
                markerDirty={markerDirty}
                infoDirty={infoDirty}
                photosDirty={photosDirty}
                docsDirty={docsDirty}
                tabsEnabled={tabsEnabled}
              />
            )}
          </Box>

          {/* 2. TOP CONTROL BAR (Floating Menu) */}
          <Box
            className='top-control-bar'
            sx={{
              position: 'absolute',
              top: 0,
              left: `${SIDEBAR_WIDTH}px`,
              width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
              height: '45px',
              backgroundColor: '#f4f4f4',
              boxShadow: 'none',
              borderBottom: '1px solid #ccc',
              boxSizing: 'border-box',
              pointerEvents: 'auto',
              zIndex: 50,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              borderRadius: isAppFullScreen ? '0 0 8px 8px' : '0',
            }}>
            <Box
              sx={{
                width: isAppFullScreen ? `${containerSize - SIDEBAR_WIDTH}px` : '100%',
                height: '100%',
                transition: 'width 0.3s ease',
              }}>
              {['MARKERS', 'POLYGON', 'POLYLINE', 'VIS_HOVER'].includes(activeTab) && (
                <MarkersTabMenu
                  handleHeaderAction={handleActionWrapper}
                  activeAction={effectiveMenuAction}
                  selectedMarker={selectedMarker || tempData}
                  isDirty={isDirtyGlobal}
                  pointsPlaced={pointsPlaced || isDirtyGlobal}
                  activeTab={activeTab}
                  isAppFullScreen={isAppFullScreen}
                />
              )}
              {activeTab === 'INFO' && (
                <InfoTabMenu
                  handleHeaderAction={handleActionWrapper}
                  isEditing={isInfoEditing}
                  selectedMarker={selectedMarker}
                  isDirty={isDirtyGlobal}
                  isAppFullScreen={isAppFullScreen}
                />
              )}
              {activeTab === 'PHOTOS' && (
                <PhotosTabMenu
                  handleHeaderAction={handleActionWrapper}
                  selectedMarker={selectedMarker}
                  isDirty={isDirtyGlobal}
                  isAppFullScreen={isAppFullScreen}
                />
              )}
              {activeTab === 'DOCS' && (
                <DocsTabMenu
                  handleHeaderAction={handleActionWrapper}
                  selectedMarker={selectedMarker}
                  isDirty={isDirtyGlobal}
                  isAppFullScreen={isAppFullScreen}
                />
              )}
            </Box>
          </Box>

          {/* 3. MARKUP PANEL (Vertically Stacked Markers, Polygon, Polyline) */}
          <Box
            className='markup-panel'
            onPointerDownCapture={() => {
              if (!['MARKERS', 'POLYGON', 'POLYLINE'].includes(activeTab)) {
                setActiveTab(
                  activeTool === 'POLYGON'
                    ? 'POLYGON'
                    : activeTool === 'LINE'
                      ? 'POLYLINE'
                      : 'MARKERS',
                );
              }
            }}
            sx={{
              display: displayMarkup ? 'flex' : 'none',
              position: 'absolute',
              top: '45px',
              left: markupLayout.left,
              width: markupLayout.width,
              height: 'calc(100% - 45px)',
              backgroundColor: '#fff',
              boxShadow: markupLayout.isFocused
                ? '0px 0px 20px 5px rgba(0,0,0,0.4)'
                : '4px 4px 10px rgba(0,0,0,0.5)',
              borderRight:
                isAppFullScreen && !markupLayout.isFocused ? '1px solid #ccc' : 'none',
              pointerEvents: 'auto',
              zIndex: markupLayout.zIndex,
              overflowY: 'auto',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
            }}>
            <Box
              onMouseDown={stopProp}
              onClick={stopProp}
              onWheel={stopProp}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                p: 0,
              }}>
              {isAppFullScreen ? (
                // FULL SCREEN MODE: Show all 3 stacked, with empty states if they don't exist
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {/* MARKER SECTION */}
                  <Box sx={{ borderBottom: '4px solid #e0e0e0' }}>
                    <Typography
                      sx={{
                        backgroundColor: '#f4f4f4',
                        color: '#00008B',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        p: '8px 12px',
                        borderBottom: '1px solid #ccc',
                        textTransform: 'uppercase',
                      }}>
                      Marker Status
                    </Typography>
                    {isWaitingMarker ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontSize: '0.85rem',
                          }}>
                          Click on the map to place the marker.
                        </Typography>
                      </Box>
                    ) : shouldRenderMarker ? (
                      <MarkupControls
                        activeTool='MARKER'
                        drawingStyles={drawingStyles}
                        handleStyleChange={handleStyleChange}
                        selectedMarker={selectedMarker}
                        mode={effectiveMenuAction}
                        onPositionChange={(pos) => {
                          if (updateMarkerInState && selectedMarker) {
                            updateMarkerInState({ ...selectedMarker, position: pos });
                            handleMarkerFieldChange('position', pos);
                          }
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          color: 'text.secondary',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}>
                        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                          No marker created.
                        </Typography>
                        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                          Select 'Add New' to place a marker.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* POLYGON SECTION */}
                  <Box sx={{ borderBottom: '4px solid #e0e0e0' }}>
                    <Typography
                      sx={{
                        backgroundColor: '#f4f4f4',
                        color: '#00008B',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        p: '8px 12px',
                        borderBottom: '1px solid #ccc',
                        textTransform: 'uppercase',
                      }}>
                      Polygon Status
                    </Typography>
                    {isWaitingPolygon ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontSize: '0.85rem',
                          }}>
                          Click on the map to place the polygon start point.
                        </Typography>
                      </Box>
                    ) : shouldRenderPolygon ? (
                      <MarkupControls
                        activeTool='POLYGON'
                        drawingStyles={drawingStyles}
                        handleStyleChange={handleStyleChange}
                        selectedMarker={selectedMarker}
                        mode={effectiveMenuAction}
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          color: 'text.secondary',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}>
                        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                          No polygon created.
                        </Typography>
                        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                          Select 'Add New' to draw a polygon.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* POLYLINE SECTION */}
                  <Box>
                    <Typography
                      sx={{
                        backgroundColor: '#f4f4f4',
                        color: '#00008B',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        p: '8px 12px',
                        borderBottom: '1px solid #ccc',
                        textTransform: 'uppercase',
                      }}>
                      Polyline Status
                    </Typography>
                    {isWaitingPolyline ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontSize: '0.85rem',
                          }}>
                          Click on the map to place the line start point.
                        </Typography>
                      </Box>
                    ) : shouldRenderPolyline ? (
                      <MarkupControls
                        activeTool='LINE'
                        drawingStyles={drawingStyles}
                        handleStyleChange={handleStyleChange}
                        selectedMarker={selectedMarker}
                        mode={effectiveMenuAction}
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          color: 'text.secondary',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}>
                        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                          No polyline created.
                        </Typography>
                        <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                          Select 'Add New' to draw a polyline.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : // NORMAL MODE: Strictly context-dependent rendering
              displayWaitingForPoint ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      fontSize: '0.85rem',
                    }}>
                    Click on the map to place the{' '}
                    {effectiveTool === 'POLYGON'
                      ? 'polygon start point'
                      : effectiveTool === 'LINE'
                        ? 'line start point'
                        : 'marker'}
                  </Typography>
                </Box>
              ) : displayShowFields ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {activeTab === 'MARKERS' && shouldRenderMarker && (
                    <Box>
                      <Typography
                        sx={{
                          backgroundColor: '#f4f4f4',
                          color: '#00008B',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          p: '8px 12px',
                          borderBottom: '1px solid #ccc',
                          textTransform: 'uppercase',
                        }}>
                        Marker Status
                      </Typography>
                      <MarkupControls
                        activeTool='MARKER'
                        drawingStyles={drawingStyles}
                        handleStyleChange={handleStyleChange}
                        selectedMarker={selectedMarker}
                        mode={effectiveMenuAction}
                        onPositionChange={(pos) => {
                          if (updateMarkerInState && selectedMarker) {
                            updateMarkerInState({ ...selectedMarker, position: pos });
                            handleMarkerFieldChange('position', pos);
                          }
                        }}
                      />
                    </Box>
                  )}
                  {activeTab === 'POLYGON' && shouldRenderPolygon && (
                    <Box>
                      <Typography
                        sx={{
                          backgroundColor: '#f4f4f4',
                          color: '#00008B',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          p: '8px 12px',
                          borderBottom: '1px solid #ccc',
                          textTransform: 'uppercase',
                        }}>
                        Polygon Status
                      </Typography>
                      <MarkupControls
                        activeTool='POLYGON'
                        drawingStyles={drawingStyles}
                        handleStyleChange={handleStyleChange}
                        selectedMarker={selectedMarker}
                        mode={effectiveMenuAction}
                      />
                    </Box>
                  )}
                  {activeTab === 'POLYLINE' && shouldRenderPolyline && (
                    <Box>
                      <Typography
                        sx={{
                          backgroundColor: '#f4f4f4',
                          color: '#00008B',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          p: '8px 12px',
                          borderBottom: '1px solid #ccc',
                          textTransform: 'uppercase',
                        }}>
                        Polyline Status
                      </Typography>
                      <MarkupControls
                        activeTool='LINE'
                        drawingStyles={drawingStyles}
                        handleStyleChange={handleStyleChange}
                        selectedMarker={selectedMarker}
                        mode={effectiveMenuAction}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}>
                  <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                    Add New to create a new marker.
                  </Typography>
                  <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                    Click on a marker on the map to update.
                  </Typography>
                  <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                    Create a group of markers when in edit mode.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* 4. VIS/HOVER PANEL */}
          <Box
            className='vishover-panel'
            onPointerDownCapture={() => setActiveTab('VIS_HOVER')}
            sx={{
              display: displayVisHover ? 'block' : 'none',
              position: 'absolute',
              top: '45px',
              left: visHoverLayout.left,
              width: visHoverLayout.width,
              height: 'calc(100% - 45px)',
              backgroundColor: '#fff',
              boxShadow: visHoverLayout.isFocused
                ? '0px 0px 20px 5px rgba(0,0,0,0.4)'
                : '4px 4px 10px rgba(0,0,0,0.5)',
              borderRight:
                isAppFullScreen && !visHoverLayout.isFocused ? '1px solid #ccc' : 'none',
              pointerEvents: 'auto',
              overflowY: 'auto',
              zIndex: visHoverLayout.zIndex,
              transition: 'all 0.3s ease',
            }}>
            {/* If there are no fields to show (e.g. no selection), display fallback unconditionally */}
            {!displayShowFields ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Click on a marker to add information, photos and documents.
                </Typography>
              </Box>
            ) : (
              <VisHoverTab
                tempData={tempData}
                isMarkerEditing={isMarkerEditing}
                handleMarkerFieldChange={handleMarkerFieldChange}
                fileInputRef={fileInputRef}
                handlePhotoUpload={handlePhotoUpload}
                handleClearPhoto={handleClearPhoto}
                stopProp={stopProp}
              />
            )}
          </Box>

          {/* 5. INFO PANEL */}
          <Box
            className='info-panel'
            onPointerDownCapture={() => setActiveTab('INFO')}
            sx={{
              display: displayInfo ? 'block' : 'none',
              position: 'absolute',
              top: '45px',
              left: infoLayout.left,
              width: infoLayout.width,
              height: 'calc(100% - 45px)',
              backgroundColor: '#fff',
              boxShadow: infoLayout.isFocused
                ? '0px 0px 20px 5px rgba(0,0,0,0.4)'
                : '4px 4px 10px rgba(0,0,0,0.5)',
              borderRight:
                isAppFullScreen && !infoLayout.isFocused ? '1px solid #ccc' : 'none',
              pointerEvents: 'auto',
              overflowY: 'auto',
              zIndex: infoLayout.zIndex,
              transition: 'all 0.3s ease',
            }}>
            {!selectedMarker ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Click on a marker to add information.
                </Typography>
              </Box>
            ) : (
              <InfoTab
                data={tempData}
                isEditing={isInfoEditing}
                onDataChange={handleInfoTabChange}
                isSaving={isSaving}
                activeMenu={activeMenu}
              />
            )}
          </Box>

          {/* 6. PHOTOS PANEL (Includes absolute positioning wrapper internally) */}
          <PhotosTab
            activeTab={activeTab}
            selectedMarker={selectedMarker}
            triggerAdd={photosTrigger}
            triggerDelete={photosDeleteTrigger}
            items={photoItems}
            setItems={setPhotoItems}
            descriptions={photoDescriptions}
            setDescriptions={setPhotoDescriptions}
            selectedIds={photoSelectedIds}
            setSelectedIds={setPhotoSelectedIds}
            lastSelectedId={photoLastSelectedId}
            setLastSelectedId={setPhotoLastSelectedId}
            isSaving={isSaving}
            panelLeft={photosLayout.left}
            panelWidth={photosLayout.width}
            isFocused={photosLayout.isFocused}
            panelZIndex={photosLayout.zIndex}
            isAppFullScreen={isAppFullScreen}
            displayPanel={displayPhotos}
            onInteract={() => setActiveTab('PHOTOS')}
            rteFontType={rteFontType} // AMENDED: Pass Global Font Type
            rteFontSize={rteFontSize} // AMENDED: Pass Global Font Size
          />

          {/* 7. DOCS PANEL */}
          <Box
            className='docs-panel'
            onPointerDownCapture={() => setActiveTab('DOCS')}
            sx={{
              display: displayDocs ? 'block' : 'none',
              position: 'absolute',
              top: '45px',
              left: docsLayout.left,
              width: docsLayout.width,
              height: 'calc(100% - 45px)',
              backgroundColor: '#fff',
              boxShadow: docsLayout.isFocused
                ? '0px 0px 20px 5px rgba(0,0,0,0.4)'
                : '4px 4px 10px rgba(0,0,0,0.5)',
              borderRight:
                isAppFullScreen && !docsLayout.isFocused ? '1px solid #ccc' : 'none',
              pointerEvents: 'auto',
              overflowY: 'auto',
              zIndex: docsLayout.zIndex,
              transition: 'all 0.3s ease',
            }}>
            {!selectedMarker ? (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Click on a marker to add documents.
                </Typography>
              </Box>
            ) : (
              <DocsTab
                selectedMarker={selectedMarker}
                triggerAdd={docsTrigger}
                triggerDelete={docsDeleteTrigger}
                items={docItems}
                setItems={setDocItems}
                selectedIds={docSelectedIds}
                setSelectedIds={setDocSelectedIds}
                isSaving={isSaving}
              />
            )}
          </Box>

          {/* 8. DYNAMIC RESIZE HANDLE */}
          {!isAppFullScreen && showPanels && (
            <Box
              className='resize-handle'
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handleResizePointerUp}
              onPointerCancel={handleResizePointerUp}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0, // Flush right against full width panels
                width: '30px',
                height: '30px',
                cursor: 'nwse-resize',
                zIndex: 100,
                pointerEvents: 'auto',
                touchAction: 'none',
                background: showPhotosNormal
                  ? 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.8) 50%)'
                  : 'linear-gradient(135deg, transparent 50%, rgba(128,128,128,0.8) 50%)',
                '&:hover': {
                  background: showPhotosNormal
                    ? 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,1) 50%)'
                    : 'linear-gradient(135deg, transparent 50%, rgba(128,128,128,1) 50%)',
                },
              }}
              title='Drag to resize'
            />
          )}
        </Box>
      </Box>
    </Portal>
  );
}
